/**
 * AUTO-GENERATED FILE - DO NOT EDIT DIRECTLY
 *
 * Generated from: permission-bits.json (single source of truth)
 * Run: npm run generate:permissions
 *
 * Bitmask permission encoding/decoding (ADR-008).
 * Bit positions MUST match the backend Permission.Bit enum exactly.
 * New permissions are always appended at the end.
 */

export const PERMISSION_BITS: Record<string, number> = {
  // User Management
  'users.create': 0,
  'users.read': 1,
  'users.update': 2,
  'users.delete': 3,
  'users.invite': 4,

  // Role Management
  'roles.create': 5,
  'roles.read': 6,
  'roles.update': 7,
  'roles.delete': 8,

  // Client Management
  'clients.create': 9,
  'clients.read': 10,
  'clients.read.all': 11,
  'clients.update': 12,
  'clients.update.own': 13,
  'clients.delete': 14,
  'clients.assign': 15,

  // Client States
  'client_states.create': 87,
  'client_states.read': 88,
  'client_states.update': 89,
  'client_states.delete': 90,

  // Client Groups
  'client_groups.create': 99,
  'client_groups.read': 100,
  'client_groups.update': 101,
  'client_groups.delete': 102,
  'client_groups.manage_members': 103,

  // Appointments
  'appointments.create': 16,
  'appointments.create.own': 17,
  'appointments.read': 18,
  'appointments.update': 19,
  'appointments.update.own': 20,
  'appointments.delete': 21,
  'appointments.delete.own': 22,
  'appointments.create.any': 82,
  'appointments.update.any': 118,

  // Appointment Types
  'appointment_types.create': 83,
  'appointment_types.read': 84,
  'appointment_types.update': 85,
  'appointment_types.delete': 86,

  // Reports (Verslagen)
  'reports.create': 23,
  'reports.read': 24,
  'reports.read.own': 25,
  'reports.update': 26,
  'reports.update.own': 27,
  'reports.delete': 28,
  'reports.finalize': 29,

  // Documents
  'documents.create': 30,
  'documents.read': 31,
  'documents.read.own': 32,
  'documents.update': 33,
  'documents.update.own': 34,
  'documents.delete': 35,
  'documents.manage_labels': 36,
  'documents.expose': 93,

  // Tasks
  'tasks.create': 37,
  'tasks.read': 38,
  'tasks.read.own': 39,
  'tasks.update': 40,
  'tasks.update.own': 41,
  'tasks.delete': 42,
  'tasks.assign': 43,

  // Care Trajectories
  'care_trajectories.create': 44,
  'care_trajectories.read': 45,
  'care_trajectories.read.own': 46,
  'care_trajectories.update': 47,
  'care_trajectories.update.own': 48,
  'care_trajectories.delete': 49,

  // Contracts
  'contracts.create': 50,
  'contracts.read': 51,
  'contracts.update': 52,
  'contracts.delete': 53,

  // Billing (Standard CRUD pattern)
  'billing.create': 54,
  'billing.read': 55,
  'billing.update': 56,
  'billing.delete': 57,
  'billing.export': 59,

  // Recordings & Transcriptions
  'recordings.create': 62,
  'recordings.read': 63,
  'recordings.read.own': 64,
  'recordings.delete': 65,

  // Team Management
  'team.manage': 66,
  'team.read': 67,

  // Locations
  'locations.create': 68,
  'locations.read': 69,
  'locations.update': 70,
  'locations.delete': 71,

  // Rooms
  'rooms.create': 78,
  'rooms.read': 79,
  'rooms.update': 80,
  'rooms.delete': 81,

  // Organization Settings
  'organization.settings.read': 72,
  'organization.settings.update': 73,

  // Security Settings
  'security.view': 74,
  'security.manage': 75,

  // Audit & Compliance
  'audit.read': 76,
  'btg.approve': 77,

  // Forms & Questions
  'questions.manage': 91,
  'forms.manage': 92,
  'forms.read': 108,
  'forms.assign': 94,
  'forms.link_appointment_type': 95,
  'forms.edit': 96,
  'forms.delete': 109,
  'forms.submit': 123,

  // Letter Templates
  'letter_templates.manage': 97,

  // Letters
  'letters.generate': 98,

  // Signals
  'signals.read': 104,
  'signal_definitions.read': 105,
  'signal_definitions.manage': 106,
  'signals.suppress': 127,

  // Transcripts
  'transcripts.delete': 107,

  // Admin Portal
  'admin.organizations.view': 110,
  'admin.organizations.create': 111,
  'admin.users.view': 112,
  'admin.organizations.manage': 113,
  'admin.users.manage': 114,
  'admin.support_agents.view': 115,
  'admin.users.impersonate': 117,

  // Integrations
  'integrations.manage': 116,
  'integrations.zorgdomein_activation.read': 126,
  'zorgmail_inbox.read': 130,
  'zorgmail_inbox.send': 131,

  // Bulk Data Imports
  'imports.zpm': 119,
  'imports.zpm.commit': 120,
  'imports.agenda': 121,
  'imports.agenda.commit': 122,
  'imports.epd': 124,
  'imports.contract_tariffs': 125,

  // Scrivio Subscription
  'subscription.read': 128,
  'subscription.manage': 129,
}

/**
 * Permission constants for type-safe permission checks.
 *
 * Usage:
 * ```typescript
 * import { Permission } from '@/utils/permissionBitmask'
 * hasPermission(Permission.USERS_CREATE)
 * ```
 */
export const Permission = {
  // User Management
  USERS_CREATE: 'users.create',
  USERS_READ: 'users.read',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  USERS_INVITE: 'users.invite',

  // Role Management
  ROLES_CREATE: 'roles.create',
  ROLES_READ: 'roles.read',
  ROLES_UPDATE: 'roles.update',
  ROLES_DELETE: 'roles.delete',

  // Client Management
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_READ: 'clients.read',
  CLIENTS_READ_ALL: 'clients.read.all',
  CLIENTS_UPDATE: 'clients.update',
  CLIENTS_UPDATE_OWN: 'clients.update.own',
  CLIENTS_DELETE: 'clients.delete',
  CLIENTS_ASSIGN: 'clients.assign',

  // Client States
  CLIENT_STATES_CREATE: 'client_states.create',
  CLIENT_STATES_READ: 'client_states.read',
  CLIENT_STATES_UPDATE: 'client_states.update',
  CLIENT_STATES_DELETE: 'client_states.delete',

  // Client Groups
  CLIENT_GROUPS_CREATE: 'client_groups.create',
  CLIENT_GROUPS_READ: 'client_groups.read',
  CLIENT_GROUPS_UPDATE: 'client_groups.update',
  CLIENT_GROUPS_DELETE: 'client_groups.delete',
  CLIENT_GROUPS_MANAGE_MEMBERS: 'client_groups.manage_members',

  // Appointments
  APPOINTMENTS_CREATE: 'appointments.create',
  APPOINTMENTS_CREATE_OWN: 'appointments.create.own',
  APPOINTMENTS_READ: 'appointments.read',
  APPOINTMENTS_UPDATE: 'appointments.update',
  APPOINTMENTS_UPDATE_OWN: 'appointments.update.own',
  APPOINTMENTS_DELETE: 'appointments.delete',
  APPOINTMENTS_DELETE_OWN: 'appointments.delete.own',
  APPOINTMENTS_CREATE_ANY: 'appointments.create.any',
  APPOINTMENTS_UPDATE_ANY: 'appointments.update.any',

  // Appointment Types
  APPOINTMENT_TYPES_CREATE: 'appointment_types.create',
  APPOINTMENT_TYPES_READ: 'appointment_types.read',
  APPOINTMENT_TYPES_UPDATE: 'appointment_types.update',
  APPOINTMENT_TYPES_DELETE: 'appointment_types.delete',

  // Reports (Verslagen)
  REPORTS_CREATE: 'reports.create',
  REPORTS_READ: 'reports.read',
  REPORTS_READ_OWN: 'reports.read.own',
  REPORTS_UPDATE: 'reports.update',
  REPORTS_UPDATE_OWN: 'reports.update.own',
  REPORTS_DELETE: 'reports.delete',
  REPORTS_FINALIZE: 'reports.finalize',

  // Documents
  DOCUMENTS_CREATE: 'documents.create',
  DOCUMENTS_READ: 'documents.read',
  DOCUMENTS_READ_OWN: 'documents.read.own',
  DOCUMENTS_UPDATE: 'documents.update',
  DOCUMENTS_UPDATE_OWN: 'documents.update.own',
  DOCUMENTS_DELETE: 'documents.delete',
  DOCUMENTS_MANAGE_LABELS: 'documents.manage_labels',
  DOCUMENTS_EXPOSE: 'documents.expose',

  // Tasks
  TASKS_CREATE: 'tasks.create',
  TASKS_READ: 'tasks.read',
  TASKS_READ_OWN: 'tasks.read.own',
  TASKS_UPDATE: 'tasks.update',
  TASKS_UPDATE_OWN: 'tasks.update.own',
  TASKS_DELETE: 'tasks.delete',
  TASKS_ASSIGN: 'tasks.assign',

  // Care Trajectories
  CARE_TRAJECTORIES_CREATE: 'care_trajectories.create',
  CARE_TRAJECTORIES_READ: 'care_trajectories.read',
  CARE_TRAJECTORIES_READ_OWN: 'care_trajectories.read.own',
  CARE_TRAJECTORIES_UPDATE: 'care_trajectories.update',
  CARE_TRAJECTORIES_UPDATE_OWN: 'care_trajectories.update.own',
  CARE_TRAJECTORIES_DELETE: 'care_trajectories.delete',

  // Contracts
  CONTRACTS_CREATE: 'contracts.create',
  CONTRACTS_READ: 'contracts.read',
  CONTRACTS_UPDATE: 'contracts.update',
  CONTRACTS_DELETE: 'contracts.delete',

  // Billing (Standard CRUD pattern)
  BILLING_CREATE: 'billing.create',
  BILLING_READ: 'billing.read',
  BILLING_UPDATE: 'billing.update',
  BILLING_DELETE: 'billing.delete',
  BILLING_EXPORT: 'billing.export',

  // Recordings & Transcriptions
  RECORDINGS_CREATE: 'recordings.create',
  RECORDINGS_READ: 'recordings.read',
  RECORDINGS_READ_OWN: 'recordings.read.own',
  RECORDINGS_DELETE: 'recordings.delete',

  // Team Management
  TEAM_MANAGE: 'team.manage',
  TEAM_READ: 'team.read',

  // Locations
  LOCATIONS_CREATE: 'locations.create',
  LOCATIONS_READ: 'locations.read',
  LOCATIONS_UPDATE: 'locations.update',
  LOCATIONS_DELETE: 'locations.delete',

  // Rooms
  ROOMS_CREATE: 'rooms.create',
  ROOMS_READ: 'rooms.read',
  ROOMS_UPDATE: 'rooms.update',
  ROOMS_DELETE: 'rooms.delete',

  // Organization Settings
  ORGANIZATION_SETTINGS_READ: 'organization.settings.read',
  ORGANIZATION_SETTINGS_UPDATE: 'organization.settings.update',

  // Security Settings
  SECURITY_VIEW: 'security.view',
  SECURITY_MANAGE: 'security.manage',

  // Audit & Compliance
  AUDIT_READ: 'audit.read',
  BTG_APPROVE: 'btg.approve',

  // Forms & Questions
  QUESTIONS_MANAGE: 'questions.manage',
  FORMS_MANAGE: 'forms.manage',
  FORMS_READ: 'forms.read',
  FORMS_ASSIGN: 'forms.assign',
  FORMS_LINK_APPOINTMENT_TYPE: 'forms.link_appointment_type',
  FORMS_EDIT: 'forms.edit',
  FORMS_DELETE: 'forms.delete',
  FORMS_SUBMIT: 'forms.submit',

  // Letter Templates
  LETTER_TEMPLATES_MANAGE: 'letter_templates.manage',

  // Letters
  LETTERS_GENERATE: 'letters.generate',

  // Signals
  SIGNALS_READ: 'signals.read',
  SIGNAL_DEFINITIONS_READ: 'signal_definitions.read',
  SIGNAL_DEFINITIONS_MANAGE: 'signal_definitions.manage',
  SIGNALS_SUPPRESS: 'signals.suppress',

  // Transcripts
  TRANSCRIPTS_DELETE: 'transcripts.delete',

  // Admin Portal
  ADMIN_ORGANIZATIONS_VIEW: 'admin.organizations.view',
  ADMIN_ORGANIZATIONS_CREATE: 'admin.organizations.create',
  ADMIN_USERS_VIEW: 'admin.users.view',
  ADMIN_ORGANIZATIONS_MANAGE: 'admin.organizations.manage',
  ADMIN_USERS_MANAGE: 'admin.users.manage',
  ADMIN_SUPPORT_AGENTS_VIEW: 'admin.support_agents.view',
  ADMIN_USERS_IMPERSONATE: 'admin.users.impersonate',

  // Integrations
  INTEGRATIONS_MANAGE: 'integrations.manage',
  INTEGRATIONS_ZORGDOMEIN_ACTIVATION_READ: 'integrations.zorgdomein_activation.read',
  ZORGMAIL_INBOX_READ: 'zorgmail_inbox.read',
  ZORGMAIL_INBOX_SEND: 'zorgmail_inbox.send',

  // Bulk Data Imports
  IMPORTS_ZPM: 'imports.zpm',
  IMPORTS_ZPM_COMMIT: 'imports.zpm.commit',
  IMPORTS_AGENDA: 'imports.agenda',
  IMPORTS_AGENDA_COMMIT: 'imports.agenda.commit',
  IMPORTS_EPD: 'imports.epd',
  IMPORTS_CONTRACT_TARIFFS: 'imports.contract_tariffs',

  // Scrivio Subscription
  SUBSCRIPTION_READ: 'subscription.read',
  SUBSCRIPTION_MANAGE: 'subscription.manage',
} as const

export type PermissionValue = (typeof Permission)[keyof typeof Permission]

// Reverse lookup: position → permission key
const BIT_TO_PERMISSION: Map<number, string> = new Map(
  Object.entries(PERMISSION_BITS).map(([key, pos]) => [pos, key]),
)

/**
 * Decode a hex-encoded bitmask string to an array of permission strings.
 */
export function decodePermissions(hex: string): string[] {
  if (!hex) return []
  const mask = BigInt(`0x${hex}`)
  const result: string[] = []
  for (const [position, key] of BIT_TO_PERMISSION) {
    if (mask & (1n << BigInt(position))) {
      result.push(key)
    }
  }
  return result
}
